import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import moment from 'moment-timezone';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import {
  COMMENT_MAX,
  CommentAuthor,
  HANDLE_PATTERN,
  moderationBadge,
  normalizeHandle,
  MAX_POST_PHOTOS,
  POST_CONTENT_MAX,
  POST_PHOTO_TYPES,
  POST_TYPE_LABELS,
  PostComment,
  RestaurantPost,
  RestaurantPostType,
  RestaurantPostUpdate,
  RestaurantWallProfile,
  RestaurantWallProfileUpdate,
  TAGLINE_MAX,
} from '../../../models/social';
import { RestaurantService } from '../../../services/restaurant.service';
import { SocialMediaService } from '../../../services/social-media.service';
import {
  croppedImageFile,
  sourceImageError,
  uploadSizeError,
} from '../../../utils/image-export.util';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { ImageCropingComponent } from '../../modals/image-croping/image-croping.component';

type TabKey = 'profile' | 'posts';

interface DraftPhoto {
  preview: string;
  objectUrl: string | null;
  uploading: boolean;
  error: string | null;
}

/** A post as the Wall card beside the page draws it. */
interface PostPreview {
  /** Which post this is, shown beside the preview's label. */
  source: 'Draft' | 'Editing' | 'Selected post';
  content: string;
  photos: string[];
  when: string;
  likes: number;
  comments: number;
  inspired: number;
}

/** A post's comments in the list (FNF-1697). */
interface CommentThread {
  open: boolean;
  loading: boolean;
  error: boolean;
  comments: PostComment[];
}

/** A comment's replies, loaded a page at a time. */
interface ReplyThread {
  open: boolean;
  loading: boolean;
  error: boolean;
  items: PostComment[];
  cursor: string | null;
  hasMore: boolean;
}

/** Whose photos: the composer's draft or the post being edited. */
type PhotoTarget = 'draft' | 'edit';

/** Frees a photo's local preview; a post's own photos are plain URLs. */
function revokePreview(photo: DraftPhoto) {
  if (photo.preview.startsWith('blob:')) URL.revokeObjectURL(photo.preview);
}

/** The posts list's filters, by moderation badge tone. */
type PostFilter = 'all' | 'live' | 'review' | 'down';

const POST_FILTERS: { key: PostFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'review', label: 'In review' },
  { key: 'down', label: 'Removed' },
];

/** The composer's counter only shows once the text nears the limit. */
const COUNTER_FROM = POST_CONTENT_MAX - 200;

const TAB_STORAGE_KEY = 'socialMedia.activeTab';

/**
 * Restaurant → Social Media (FNF-1609): the restaurant's Fynkle Wall Profile
 * and the posts it publishes to the Wall.
 *
 * The profile is created by the platform when the restaurant is published on
 * Fynkle (FNF-1686), so before that both tabs explain why there is nothing to
 * manage yet and the composer stays off. The Wall Profile tab edits the
 * handle, tagline and cover (FNF-1683); the name and logo come from
 * Restaurant Profile. Post photos are uploaded straight to S3 (FNF-1688).
 * Beside the forms, previews show the Wall Profile (Wall Profile tab) or a
 * post — the draft, the one being edited or the one picked in the list
 * (Posts tab) — as diners see them on the Wall.
 */
@Component({
  selector: 'app-social-media',
  standalone: true,
  imports: [CommonModule, FormsModule, MatMenuModule],
  templateUrl: './social-media.component.html',
  styleUrl: './social-media.component.css',
})
export class SocialMediaComponent implements OnInit, OnDestroy {
  private socialMediaService = inject(SocialMediaService);
  private restaurantService = inject(RestaurantService);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  private dialog = inject(MatDialog);

  readonly contentMax = POST_CONTENT_MAX;
  readonly postTypes: RestaurantPostType[] = ['general', 'recommendation'];
  readonly typeLabels = POST_TYPE_LABELS;
  readonly maxPhotos = MAX_POST_PHOTOS;
  readonly photoAccept = POST_PHOTO_TYPES.join(',');
  readonly taglineMax = TAGLINE_MAX;
  readonly postFilters = POST_FILTERS;

  activeTab: TabKey = 'posts';
  restaurantId: string | null = null;
  restaurantTimezone: string | undefined;

  profile: RestaurantWallProfile | null = null;
  profileLoading = false;
  /** No profile yet: the restaurant isn't published on Fynkle (FNF-1702). */
  notLive = false;

  posts: RestaurantPost[] = [];
  postsCursor: string | null = null;
  postsLoading = false;

  draft = { content: '', type: 'general' as RestaurantPostType };
  /** The composer's photos (FNF-1688): each uploads as soon as it is picked. */
  draftPhotos: DraftPhoto[] = [];
  publishing = false;

  editingId: string | null = null;
  editContent = '';
  /** The edited post's photos: its own, plus any added while editing. */
  editPhotos: DraftPhoto[] = [];
  saving = false;

  /** The post picked in the list, shown in the Post preview. */
  selectedPostId: string | null = null;
  postFilter: PostFilter = 'all';
  /** A post photo opened full size. */
  lightboxUrl: string | null = null;
  /** Photos are being dragged over the composer. */
  dragging = false;

  /** Each post's comments, by postId, once opened (FNF-1697). */
  threads: Partial<Record<string, CommentThread>> = {};
  /** Each comment's replies, by commentId, once opened. */
  replies: Partial<Record<string, ReplyThread>> = {};

  readonly commentMax = COMMENT_MAX;
  /** The comment or reply being answered as the restaurant (FNF-1698). */
  replyingTo: PostComment | null = null;
  replyText = '';
  sendingReply = false;
  /** Each post's new top-level comment, by postId. */
  newComment: Record<string, string> = {};
  sendingComment = false;

  /** Wall Profile form (FNF-1683): what staff typed, saved together. */
  profileForm = { username: '', bio: '', coverImageUrl: null as string | null };
  handleError: string | null = null;
  coverError: string | null = null;
  savingProfile = false;
  uploadingCover = false;

  private restaurantSub?: Subscription;

  ngOnInit() {
    this.activeTab = this.storedTab();
    this.restaurantSub = this.restaurantService.currentRestaurantDetails$.subscribe(
      (restaurant: any) => {
        this.restaurantTimezone = restaurant?.time_zone;
        const id = restaurant?.restaurant_id || null;
        if (id && id !== this.restaurantId) {
          // A switched restaurant is a different Wall: start over.
          this.restaurantId = id;
          this.reload();
        }
      },
    );
  }

  ngOnDestroy() {
    this.restaurantSub?.unsubscribe();
    this.clearDraftPhotos();
    this.cancelEdit();
  }

  // --- Tabs ---------------------------------------------------------------

  selectTab(tab: TabKey) {
    this.activeTab = tab;
    try {
      localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
      // Storage off: the tab just isn't remembered.
    }
  }

  private storedTab(): TabKey {
    try {
      const stored = localStorage.getItem(TAB_STORAGE_KEY);
      return stored === 'profile' || stored === 'posts' ? stored : 'posts';
    } catch {
      return 'posts';
    }
  }

  // --- Loading ------------------------------------------------------------

  reload() {
    this.clearDraftPhotos();
    this.profile = null;
    this.resetProfileForm();
    this.notLive = false;
    this.posts = [];
    this.postsCursor = null;
    this.selectedPostId = null;
    this.postFilter = 'all';
    this.threads = {};
    this.replies = {};
    this.newComment = {};
    this.cancelReply();
    this.cancelEdit();
    this.loadProfile();
  }

  private loadProfile() {
    if (!this.restaurantId) return;
    this.profileLoading = true;
    this.socialMediaService.getProfile(this.restaurantId).subscribe((response: any) => {
      this.profileLoading = false;
      if (response?.data) {
        this.setProfile(response.data);
        this.loadPosts(true);
        return;
      }
      if (response?.status === 404) {
        this.notLive = true;
        return;
      }
      this.showError(response, 'Could not load your Wall Profile');
    });
  }

  loadPosts(reset = false) {
    if (!this.restaurantId) return;
    if (reset) {
      this.posts = [];
      this.postsCursor = null;
    }
    this.postsLoading = true;
    this.socialMediaService
      .getPosts(this.restaurantId, this.postsCursor)
      .subscribe((response: any) => {
        this.postsLoading = false;
        if (!response?.data) {
          this.showError(response, 'Could not load your posts');
          return;
        }
        this.posts = [...this.posts, ...(response.data.items || [])];
        this.postsCursor = response.data.nextCursor || null;
      });
  }

  // --- Wall Profile (FNF-1683) --------------------------------------------

  private setProfile(profile: RestaurantWallProfile) {
    this.profile = profile;
    this.resetProfileForm();
  }

  resetProfileForm() {
    this.profileForm = {
      username: this.profile?.username || '',
      bio: this.profile?.bio || '',
      coverImageUrl: this.profile?.coverImageUrl || null,
    };
    this.handleError = null;
    this.coverError = null;
  }

  /** Still the defaults it was created with: no tagline and no cover yet. */
  get profileNeedsPersonalising(): boolean {
    return !!this.profile && !this.profile.bio && !this.profile.coverImageUrl;
  }

  /** Only what changed, so an untouched handle is never re-validated. */
  get profileChanges(): RestaurantWallProfileUpdate {
    if (!this.profile) return {};
    const changes: RestaurantWallProfileUpdate = {};
    const username = normalizeHandle(this.profileForm.username);
    if (username && username !== this.profile.username) changes.username = username;
    const bio = this.profileForm.bio.trim() || null;
    if (bio !== (this.profile.bio || null)) changes.bio = bio;
    if (this.profileForm.coverImageUrl !== (this.profile.coverImageUrl || null)) {
      changes.coverImageUrl = this.profileForm.coverImageUrl;
    }
    return changes;
  }

  get canSaveProfile(): boolean {
    return (
      !this.savingProfile &&
      !this.uploadingCover &&
      Object.keys(this.profileChanges).length > 0 &&
      !this.handleProblem() &&
      this.profileForm.bio.length <= this.taglineMax
    );
  }

  /** The client-side half of the server's rule; taken/reserved come back as 409/400. */
  handleProblem(): string | null {
    const username = normalizeHandle(this.profileForm.username);
    if (!username) return 'Choose a handle';
    if (!HANDLE_PATTERN.test(username)) {
      return 'Use 3–30 characters: lowercase letters, numbers, _ and .';
    }
    return null;
  }

  saveProfile() {
    if (!this.canSaveProfile || !this.restaurantId) return;
    const changes = this.profileChanges;
    this.savingProfile = true;
    this.handleError = null;
    this.socialMediaService.updateProfile(this.restaurantId, changes).subscribe((response: any) => {
      this.savingProfile = false;
      if (response?.data) {
        this.setProfile(response.data);
        this.toastr.success('Wall Profile updated', 'Success');
        return;
      }
      if (response?.status === 409 && changes.username) {
        this.handleError = 'That handle is taken. Try another.';
        return;
      }
      if (response?.status === 400 && changes.username) {
        this.handleError = 'That handle is not allowed. Try another.';
        return;
      }
      if (response?.status === 404) {
        this.notLive = true;
      }
      this.showError(response, 'Could not save your Wall Profile');
    });
  }

  onCoverFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // so the same file can be picked again
    if (!file) return;
    const sourceError = sourceImageError(file);
    if (sourceError) {
      this.coverError = sourceError;
      return;
    }
    this.coverError = null;
    const reader = new FileReader();
    reader.onload = () => this.openCoverCrop(reader.result as string);
    reader.readAsDataURL(file);
  }

  private openCoverCrop(imageSrc: string) {
    const dialogRef = this.dialog.open(ImageCropingComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        imageSrc,
        // The Fynkle profile header is about 2:1 (Figma "Fynkle Wall - Profile").
        targetWidth: 800,
        targetHeight: 400,
        title: 'Crop Wall Profile Cover',
        isImageFrom: 'wall-profile-cover',
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result?.blob) return;
      const tooLarge = uploadSizeError(result.blob);
      if (tooLarge) {
        this.coverError = tooLarge;
        return;
      }
      this.uploadCover(croppedImageFile(result.blob, 'wall-profile-cover'));
    });
  }

  private uploadCover(file: File) {
    const formData = new FormData();
    formData.append('file_id', 'wall-profile-cover');
    formData.append('file_type', 'profiles');
    formData.append('is_public', 'true');
    formData.append('file', file);
    this.uploadingCover = true;
    this.restaurantService.addImage(formData).subscribe((response: any) => {
      this.uploadingCover = false;
      if (!response?.data?.url) {
        this.coverError = 'Could not upload the cover. Please try again.';
        return;
      }
      // Shown in the preview now; saved with the rest of the form.
      this.profileForm.coverImageUrl = response.data.url;
    });
  }

  removeCover() {
    this.profileForm.coverImageUrl = null;
    this.coverError = null;
  }

  // --- Composer -----------------------------------------------------------

  get canPublish(): boolean {
    const length = this.draft.content.trim().length;
    return (
      !!this.profile &&
      !this.profile.isHidden &&
      !this.publishing &&
      !this.draftPhotos.some((photo) => photo.uploading) &&
      length > 0 &&
      length <= this.contentMax
    );
  }

  get uploadedPhotoUrls(): string[] {
    return this.draftPhotos.filter((p) => p.objectUrl).map((p) => p.objectUrl as string);
  }

  get canAddPhotos(): boolean {
    return this.draftPhotos.length < this.maxPhotos && !this.publishing;
  }

  get canAddEditPhotos(): boolean {
    return this.editPhotos.length < this.maxPhotos && !this.saving;
  }

  get showCounter(): boolean {
    return this.draft.content.length >= COUNTER_FROM;
  }

  /** Why Post is off, as its tooltip; empty when it is on. */
  get publishHint(): string {
    if (this.canPublish || this.publishing) return '';
    if (this.profile?.isHidden) return 'Your Wall Profile is hidden from diners';
    if (this.draftPhotos.some((photo) => photo.uploading)) {
      return 'Wait for your photos to finish uploading';
    }
    return 'Write something to post';
  }

  addPhotos(event: Event, target: PhotoTarget = 'draft') {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = ''; // so the same file can be picked again
    this.addFiles(files, target);
  }

  onDragOver(event: DragEvent) {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault(); // allows the drop
    this.dragging = this.canAddPhotos;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
    if (!this.canAddPhotos) return;
    this.addFiles(Array.from(event.dataTransfer?.files || []));
  }

  /** Each picked photo is checked, presigned and sent straight to S3; the
   * post then carries only the photos that finished uploading. `target` is
   * the composer's draft or the post being edited. */
  addFiles(files: File[], target: PhotoTarget = 'draft') {
    if (!this.restaurantId || !files.length) return;
    const room = this.maxPhotos - this.photosOf(target).length;
    if (files.length > room) {
      this.toastr.warning(`Up to ${this.maxPhotos} photos per post`, 'Photos');
    }
    for (const file of files.slice(0, room)) {
      const error = !POST_PHOTO_TYPES.includes(file.type)
        ? 'Use a JPEG, PNG or WebP photo.'
        : sourceImageError(file);
      const photo: DraftPhoto = {
        preview: URL.createObjectURL(file),
        objectUrl: null,
        uploading: !error,
        error,
      };
      this.setPhotos(target, [...this.photosOf(target), photo]);
      if (!error) this.uploadPhoto(photo, file);
    }
  }

  private photosOf(target: PhotoTarget): DraftPhoto[] {
    return target === 'edit' ? this.editPhotos : this.draftPhotos;
  }

  private setPhotos(target: PhotoTarget, photos: DraftPhoto[]) {
    if (target === 'edit') {
      this.editPhotos = photos;
    } else {
      this.draftPhotos = photos;
    }
  }

  private uploadPhoto(photo: DraftPhoto, file: File) {
    const failed = (message: string) => {
      photo.uploading = false;
      photo.error = message;
    };
    this.socialMediaService
      .presignPhoto(this.restaurantId!, file.type)
      .subscribe((response: any) => {
        const upload = response?.data;
        if (!upload) {
          failed('Could not start the upload.');
          return;
        }
        if (file.size > upload.maxBytes) {
          failed('Photo is larger than 10MB.');
          return;
        }
        this.socialMediaService.uploadPhoto(upload, file).subscribe((result: any) => {
          if (result !== true) {
            failed('Upload failed. Remove it and try again.');
            return;
          }
          photo.uploading = false;
          photo.objectUrl = upload.objectUrl;
        });
      });
  }

  removePhoto(photo: DraftPhoto, target: PhotoTarget = 'draft') {
    revokePreview(photo);
    this.setPhotos(
      target,
      this.photosOf(target).filter((p) => p !== photo),
    );
  }

  private clearDraftPhotos() {
    this.draftPhotos.forEach(revokePreview);
    this.draftPhotos = [];
  }

  publish() {
    if (!this.canPublish || !this.restaurantId) return;
    this.publishing = true;
    this.spinner.show();
    this.socialMediaService
      .createPost(this.restaurantId, {
        // A post with photos is a photo post; failed uploads are left out.
        type: this.uploadedPhotoUrls.length ? 'photo' : this.draft.type,
        content: this.draft.content.trim(),
        mediaUrls: this.uploadedPhotoUrls,
      })
      .subscribe((response: any) => {
        this.publishing = false;
        this.spinner.hide();
        if (response?.data) {
          this.posts = [response.data, ...this.posts];
          // Only cleared on success, so a failed publish keeps the text.
          this.draft = { content: '', type: 'general' };
          this.clearDraftPhotos();
          this.toastr.success('Posted to the Fynkle Wall', 'Success');
          return;
        }
        if (response?.status === 409) {
          this.notLive = true;
        }
        this.showError(response, 'Could not publish your post');
      });
  }

  // --- Edit / delete ------------------------------------------------------

  startEdit(post: RestaurantPost) {
    this.cancelEdit();
    this.editingId = post.postId;
    this.editContent = post.content;
    // The post's photos are already uploaded: kept as they are unless removed.
    this.editPhotos = post.mediaUrls.map((url) => ({
      preview: url,
      objectUrl: url,
      uploading: false,
      error: null,
    }));
  }

  cancelEdit() {
    this.editPhotos.forEach(revokePreview);
    this.editPhotos = [];
    this.editingId = null;
    this.editContent = '';
    this.saving = false;
  }

  get canSaveEdit(): boolean {
    const length = this.editContent.trim().length;
    return (
      !this.saving &&
      length > 0 &&
      length <= this.contentMax &&
      !this.editPhotos.some((photo) => photo.uploading)
    );
  }

  /** Only what changed. New or removed photos change the type with them
   * (a post with photos is a photo post), and send it back to moderation. */
  editChanges(post: RestaurantPost): RestaurantPostUpdate {
    const changes: RestaurantPostUpdate = {};
    const content = this.editContent.trim();
    if (content !== post.content) changes.content = content;
    // Failed uploads are left out, as when publishing.
    const mediaUrls = this.editPhotos.filter((p) => p.objectUrl).map((p) => p.objectUrl as string);
    const photosChanged =
      mediaUrls.length !== post.mediaUrls.length ||
      mediaUrls.some((url, i) => url !== post.mediaUrls[i]);
    if (photosChanged) {
      changes.mediaUrls = mediaUrls;
      if (mediaUrls.length && post.type !== 'photo') changes.type = 'photo';
      if (!mediaUrls.length && post.type === 'photo') changes.type = 'general';
    }
    return changes;
  }

  saveEdit(post: RestaurantPost) {
    if (!this.restaurantId || !this.canSaveEdit) return;
    const changes = this.editChanges(post);
    if (!Object.keys(changes).length) {
      this.cancelEdit();
      return;
    }
    this.saving = true;
    this.socialMediaService
      .updatePost(this.restaurantId, post.postId, changes)
      .subscribe((response: any) => {
        this.saving = false;
        if (!response?.data) {
          this.showError(response, 'Could not save your changes');
          return;
        }
        this.posts = this.posts.map((p) => (p.postId === post.postId ? response.data : p));
        this.cancelEdit();
        this.toastr.success('Post updated', 'Success');
      });
  }

  confirmDelete(post: RestaurantPost) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete post?',
        message: 'This removes the post from the Fynkle Wall. It cannot be undone.',
        buttonText: { ok: 'Delete', cancel: 'Cancel' },
      },
      width: '405px',
    });
    dialogRef.afterClosed().subscribe((confirmed: any) => {
      if (confirmed) this.deletePost(post);
    });
  }

  private deletePost(post: RestaurantPost) {
    if (!this.restaurantId) return;
    this.spinner.show();
    this.socialMediaService
      .deletePost(this.restaurantId, post.postId)
      .subscribe((response: any) => {
        this.spinner.hide();
        // Success is an empty 204 body; an error comes back as the error.
        if (response && (response.status >= 400 || response.error)) {
          this.showError(response, 'Could not delete the post');
          return;
        }
        this.posts = this.posts.filter((p) => p.postId !== post.postId);
        if (this.selectedPostId === post.postId) this.selectedPostId = null;
        this.toastr.success('Post deleted', 'Success');
      });
  }

  // --- Post preview -------------------------------------------------------

  /**
   * The post shown as a Wall card beside the page: the post being edited,
   * else the composer's draft, else the post picked in the list. Null shows
   * a ghost card — the list already shows the posts themselves.
   */
  get previewPost(): PostPreview | null {
    const editing = this.editingId && this.posts.find((p) => p.postId === this.editingId);
    if (editing) {
      return {
        ...this.toPreview(editing, 'Editing', this.editContent.trim(), 'Editing'),
        photos: this.editPhotos.filter((p) => !p.error).map((p) => p.preview),
      };
    }
    const content = this.draft.content.trim();
    if (content || this.draftPhotos.length) {
      return {
        source: 'Draft',
        content,
        // Local previews, so a photo shows while it is still uploading.
        photos: this.draftPhotos.filter((p) => !p.error).map((p) => p.preview),
        when: 'Just now',
        likes: 0,
        comments: 0,
        inspired: 0,
      };
    }
    const selected = this.posts.find((p) => p.postId === this.selectedPostId);
    return selected
      ? this.toPreview(
          selected,
          'Selected post',
          selected.content,
          this.formatRelative(selected.created),
        )
      : null;
  }

  private toPreview(
    post: RestaurantPost,
    source: PostPreview['source'],
    content: string,
    when: string,
  ): PostPreview {
    return {
      source,
      content,
      photos: post.mediaUrls,
      when,
      likes: post.likesCount,
      comments: post.commentsCount,
      inspired: post.inspiredCount,
    };
  }

  /** Picks a post for the preview; picking it again lets it go. */
  selectPost(post: RestaurantPost) {
    if (this.editingId === post.postId) return; // clicks in its edit box
    this.selectedPostId = this.selectedPostId === post.postId ? null : post.postId;
  }

  // --- Posts list ---------------------------------------------------------

  /** The loaded posts the current filter keeps. */
  get visiblePosts(): RestaurantPost[] {
    if (this.postFilter === 'all') return this.posts;
    return this.posts.filter((post) => this.badge(post).tone === this.postFilter);
  }

  /** How many loaded posts a filter keeps (more may follow on Load more). */
  filterCount(filter: PostFilter): number {
    if (filter === 'all') return this.posts.length;
    return this.posts.filter((post) => this.badge(post).tone === filter).length;
  }

  // --- Comments (FNF-1697) ------------------------------------------------

  /** Opens or closes a post's comments; they load the first time. */
  toggleComments(post: RestaurantPost, event?: Event) {
    event?.stopPropagation(); // not a pick of the post
    const thread = this.threads[post.postId];
    if (thread) {
      thread.open = !thread.open;
      return;
    }
    this.threads[post.postId] = { open: true, loading: false, error: false, comments: [] };
    this.loadComments(post);
  }

  loadComments(post: RestaurantPost) {
    const thread = this.threads[post.postId];
    if (!this.restaurantId || !thread) return;
    thread.loading = true;
    thread.error = false;
    this.socialMediaService
      .getComments(this.restaurantId, post.postId)
      .subscribe((response: any) => {
        thread.loading = false;
        if (!Array.isArray(response?.data)) {
          thread.error = true;
          return;
        }
        thread.comments = response.data;
      });
  }

  /** Shows a comment's replies, a page at a time; again hides them. */
  toggleReplies(post: RestaurantPost, comment: PostComment) {
    const replies = this.replies[comment.interactionId];
    if (replies) {
      replies.open = !replies.open;
      return;
    }
    this.replies[comment.interactionId] = {
      open: true,
      loading: false,
      error: false,
      items: [],
      cursor: null,
      hasMore: false,
    };
    this.loadReplies(post, comment);
  }

  loadReplies(post: RestaurantPost, comment: PostComment) {
    const replies = this.replies[comment.interactionId];
    if (!this.restaurantId || !replies) return;
    replies.loading = true;
    replies.error = false;
    this.socialMediaService
      .getCommentReplies(this.restaurantId, post.postId, comment.interactionId, replies.cursor)
      .subscribe((response: any) => {
        replies.loading = false;
        const page = response?.data;
        if (!page?.replies) {
          replies.error = true;
          return;
        }
        replies.items = [...replies.items, ...page.replies];
        replies.cursor = page.nextCursor || null;
        replies.hasMore = !!page.hasMore;
      });
  }

  // --- Replying as the restaurant (FNF-1698) ------------------------------

  startReply(comment: PostComment) {
    this.replyingTo = comment;
    this.replyText = '';
  }

  cancelReply() {
    this.replyingTo = null;
    this.replyText = '';
    this.sendingReply = false;
  }

  get canSendReply(): boolean {
    const length = this.replyText.trim().length;
    return !this.sendingReply && length > 0 && length <= this.commentMax;
  }

  /** Replies to replyingTo. A reply to a reply joins its top-level comment,
   * which the service works out; the reply comes back with that parent. */
  sendReply(post: RestaurantPost) {
    const target = this.replyingTo;
    if (!this.restaurantId || !target || !this.canSendReply) return;
    this.sendingReply = true;
    this.socialMediaService
      .createComment(this.restaurantId, post.postId, {
        content: this.replyText.trim(),
        parentCommentId: target.interactionId,
      })
      .subscribe((response: any) => {
        this.sendingReply = false;
        const reply: PostComment | undefined = response?.data;
        if (!reply?.interactionId) {
          this.showError(response, 'Could not send your reply');
          return;
        }
        this.cancelReply();
        post.commentsCount += 1;
        this.addReply(post, reply);
        this.toastr.success('Reply posted', 'Success');
      });
  }

  /** Shows a sent reply under its top-level comment. */
  private addReply(post: RestaurantPost, reply: PostComment) {
    const parentId = reply.parentCommentId;
    const parent = this.threads[post.postId]?.comments.find((c) => c.interactionId === parentId);
    if (!parentId || !parent) return;
    parent.replyCount += 1;
    const replies = this.replies[parentId];
    if (!replies) {
      // Not opened yet: load them, the new reply included.
      this.toggleReplies(post, parent);
      return;
    }
    replies.open = true;
    // Only appended once every earlier page is in, so the order holds.
    if (!replies.hasMore) replies.items = [...replies.items, reply];
  }

  /** A new top-level comment on a post, as the restaurant. */
  sendComment(post: RestaurantPost) {
    const content = (this.newComment[post.postId] || '').trim();
    if (!this.restaurantId || !content || this.sendingComment) return;
    this.sendingComment = true;
    this.socialMediaService
      .createComment(this.restaurantId, post.postId, { content })
      .subscribe((response: any) => {
        this.sendingComment = false;
        const comment: PostComment | undefined = response?.data;
        if (!comment?.interactionId) {
          this.showError(response, 'Could not post your comment');
          return;
        }
        this.newComment[post.postId] = '';
        post.commentsCount += 1;
        const thread = this.threads[post.postId];
        if (thread) thread.comments = [...thread.comments, comment];
      });
  }

  confirmDeleteComment(post: RestaurantPost, comment: PostComment) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete comment?',
        message: 'Diners will no longer see it. It cannot be undone.',
        buttonText: { ok: 'Delete', cancel: 'Cancel' },
      },
      width: '405px',
    });
    dialogRef.afterClosed().subscribe((confirmed: any) => {
      if (confirmed) this.deleteComment(post, comment);
    });
  }

  deleteComment(post: RestaurantPost, comment: PostComment) {
    if (!this.restaurantId) return;
    this.socialMediaService
      .deleteComment(this.restaurantId, post.postId, comment.interactionId)
      .subscribe((response: any) => {
        // Success is an empty 204 body; an error comes back as the error.
        if (response && (response.status >= 400 || response.error)) {
          this.showError(response, 'Could not delete the comment');
          return;
        }
        post.commentsCount = Math.max(post.commentsCount - 1, 0);
        const thread = this.threads[post.postId];
        const parentId = comment.parentCommentId;
        if (parentId) {
          const replies = this.replies[parentId];
          if (replies) {
            replies.items = replies.items.filter((r) => r.interactionId !== comment.interactionId);
          }
          const parent = thread?.comments.find((c) => c.interactionId === parentId);
          if (parent) parent.replyCount = Math.max(parent.replyCount - 1, 0);
        } else if (thread) {
          thread.comments = thread.comments.filter(
            (c) => c.interactionId !== comment.interactionId,
          );
        }
        if (this.replyingTo?.interactionId === comment.interactionId) this.cancelReply();
        this.toastr.success('Comment deleted', 'Success');
      });
  }

  authorName(author: CommentAuthor): string {
    return author.displayName || (author.username ? '@' + author.username : 'Fynkle diner');
  }

  authorInitial(author: CommentAuthor): string {
    return this.authorName(author).replace('@', '').charAt(0).toUpperCase();
  }

  openPhoto(url: string, event: Event) {
    event.stopPropagation(); // not a pick of the post
    this.lightboxUrl = url;
  }

  @HostListener('document:keydown.escape')
  closeLightbox() {
    this.lightboxUrl = null;
  }

  // --- View helpers -------------------------------------------------------

  badge(post: RestaurantPost) {
    return moderationBadge(post.moderationStatus);
  }

  /** "2 hours ago"; the full date is the tooltip. */
  formatRelative(value?: string | null): string {
    return value ? moment(value).fromNow() : '';
  }

  formatDateTime(value?: string | null): string {
    if (!value) return '';
    const timestamp = this.restaurantTimezone
      ? moment.tz(value, this.restaurantTimezone)
      : moment(value);
    return timestamp.format('MMM D, YYYY · h:mm A');
  }

  trackPost(_: number, post: RestaurantPost) {
    return post.postId;
  }

  private showError(response: any, fallback: string) {
    const message =
      response?.error?.detail ||
      response?.error?.message ||
      response?.error?.error?.message ||
      fallback;
    this.toastr.error(typeof message === 'string' ? message : fallback, 'Error');
  }
}
